import { FC, PropsWithChildren } from 'react';
import classnames from 'classnames';
import { PageErrorState } from 'frontStaticAppPath/lib/types';

import { translation, TranslatedComponent } from '@hh.ru/front-static-app';
import HSpacing from 'bloko/blocks/hSpacing';
import { H1Section } from 'bloko/blocks/header';
import { IconColor, IconDynamic, IconLink, SquaresScaleSmallIntersectFalse } from 'bloko/blocks/icon';
import Link from 'bloko/blocks/link';
import Text, { TextImportance } from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';
import { format, formatToReactComponent } from 'bloko/common/trl';

import GridContainer, { GridRow, GridColumn } from 'src/components/Grid';
import PageLayout from 'src/components/PageLayout';
import TopLevelSite from 'src/constants/topLevelSite';
import { useSelector } from 'src/modules/useSelector';

import styles from 'src/components/PageError/page-error.less';

const HANDLED_ERROR_CODES = [400, 403, 404];
const DEFAULT_ERROR_CODE = 400;

const TrlKeys = {
    copyright: 'index.footer.menuitem.copyright',
    logoTitle: 'navi.logo.title',
    header: {
        404: 'error.page.title.404',
        403: 'error.page.title.403',
        401: 'error.page.title.401',
        400: 'error.page.title.400',
        default: 'error.page.title.400',
    },
    description: {
        404: 'error.page.description.404',
        403: 'error.page.description.403',
        400: 'error.page.description.400',
        default: 'error.page.description.400',
    },
    errorCode: 'error.page.code',
    indexLink: 'error.page.link.index',
    feedbackLink: 'error.page.link.feedback',
    loginLink: 'error.page.authorization.link',
};

const RequestIdIcon: FC<
    {
        requestId: string;
    } & PropsWithChildren
> = ({ requestId }) => {
    return (
        <span>
            {requestId}
            &nbsp;
            <IconDynamic>
                <IconLink
                    onClick={() => {
                        void navigator.clipboard.writeText(requestId);
                    }}
                >
                    <SquaresScaleSmallIntersectFalse
                        initial={IconColor.Gray40}
                        highlighted={IconColor.SpecialBlackJack}
                    />
                </IconLink>
            </IconDynamic>
        </span>
    );
};

const PageError: TranslatedComponent<PageErrorState> = ({ code, trls }) => {
    const errorCode = code && HANDLED_ERROR_CODES.includes(code) ? code : DEFAULT_ERROR_CODE;

    const requestId = useSelector(({ request }) => request.id);
    const localeHost = useSelector(({ locale }) => locale.host) || 'hh.ru';

    const pageHeader = TrlKeys.header[errorCode as keyof typeof TrlKeys.header] || TrlKeys.header.default;
    const errorDescription =
        TrlKeys.description[errorCode as keyof typeof TrlKeys.description] || TrlKeys.description.default;

    return (
        <PageLayout title={trls[pageHeader]}>
            <GridContainer>
                <GridRow>
                    <GridColumn m="1" l="3" />
                    <GridColumn m="12" l="12">
                        <div className={styles.errorCenteredPage}>
                            <div className={styles.errorContainer}>
                                <VSpacing base={10} />
                                <div className={styles.errorLogoContainer}>
                                    <Link href={'/'}>
                                        <span
                                            className={classnames([styles.supernovaLogo], {
                                                [styles.supernovaLogoRabotaBy]: localeHost === 'rabota.by',
                                                [styles.supernovaLogoHrZarplataRu]: localeHost === TopLevelSite.ZP,
                                            })}
                                        />
                                    </Link>
                                </div>
                                <VSpacing base={10} />
                                <VSpacing base={5} />
                                <div className={styles.errorContentWrapper}>
                                    <div className={styles.errorContent}>
                                        <div className={styles.errorTextContent}>
                                            <VSpacing base={4} />
                                            <H1Section>{trls[pageHeader]}</H1Section>
                                            <VSpacing base={2} />
                                            <Text importance={TextImportance.Secondary}>
                                                {format(trls[TrlKeys.errorCode], {
                                                    '{0}': errorCode || '',
                                                })}
                                            </Text>
                                            <VSpacing base={4} />

                                            <div className={styles.errorText}>
                                                {formatToReactComponent(trls[errorDescription], {
                                                    '{0}': <RequestIdIcon requestId={requestId || ''} />,
                                                })}
                                            </div>

                                            <VSpacing base={6} />
                                            <div>
                                                {errorCode === 403 ? (
                                                    <Link href={'/account/login'}>{trls[TrlKeys.loginLink]}</Link>
                                                ) : (
                                                    <Link href={'/'}>{trls[TrlKeys.indexLink]}</Link>
                                                )}
                                                <HSpacing base={5} />
                                                <Link href={'https://feedback.hh.ru/ticket/add'} target="_blank">
                                                    {trls[TrlKeys.feedbackLink]}
                                                </Link>
                                            </div>
                                        </div>
                                        <div
                                            className={classnames([styles.errorPicture], {
                                                [styles.errorPictureBadRequest]: errorCode === 400,
                                                [styles.errorPictureForbidden]: errorCode === 403,
                                                [styles.errorPictureNotFound]: errorCode === 404,
                                            })}
                                        />
                                    </div>
                                </div>
                                <VSpacing base={6} />
                                <Text importance={TextImportance.Tertiary}>
                                    {format(trls[TrlKeys.copyright], {
                                        '{0}': new Date().getFullYear(),
                                    })}
                                </Text>
                                <VSpacing base={10} />
                            </div>
                        </div>
                    </GridColumn>
                </GridRow>
            </GridContainer>
        </PageLayout>
    );
};

export default translation(PageError);
